import { descope } from '@/config/descope';
import type { QueryClient } from '@tanstack/react-query';
import { isLoggingOutState, jwtTokenState } from '@/auth/auth.state';
import { clearNavbarClosedStateOnLogout } from '@/features/navbar/navbar.state';
import { resetMixpanel } from '@/lib/mixpanel/mixpanel';
import { paths } from '@/routes/helpers/paths';
import { setAtomValue } from '@/shared/jotai';

interface DataClearingOptions {
  queryClient?: QueryClient;
  setToken?: (value: string | null) => void;
  useJotaiStore?: boolean;
}

interface LogoutOptions extends DataClearingOptions {
  skipDescopeLogout?: boolean;
}

/**
 * Clears all user-related data from the application
 * Can be used in various contexts: logout, error handling, inactive accounts, etc.
 */
export const clearUserData = ({
  queryClient,
  setToken,
  useJotaiStore = false,
}: DataClearingOptions = {}) => {
  try {
    // Clear React Query cache
    if (queryClient) {
      queryClient.removeQueries();
    }

    clearNavbarClosedStateOnLogout();
    resetMixpanel();

    // Clear JWT token - support both approaches
    if (setToken) {
      setToken(null);
    } else if (useJotaiStore) {
      // Use setAtomValue to reset the atom to its default value
      // This also clears the persisted state automatically for atomWithStorage
      setAtomValue(jwtTokenState, null);
    }
  } catch (error) {
    // Log error but don't throw - data clearing should be robust
    console.warn('Error during data clearing:', error);
  }
};

const navigateToSignInPage = () => {
  window.location.assign(paths.signIn());
};

/**
 * Performs complete logout including data clearing and navigation
 */
export const performLogout = async ({
  queryClient,
  setToken,
  skipDescopeLogout = false,
  useJotaiStore = false,
}: LogoutOptions) => {
  try {
    setAtomValue(isLoggingOutState, true);

    if (queryClient) {
      await queryClient.cancelQueries();
    }

    clearUserData({ queryClient, setToken, useJotaiStore });

    if (!skipDescopeLogout) {
      await descope.logout();
    }

    navigateToSignInPage();
  } catch {
    clearUserData({ queryClient, setToken, useJotaiStore });
    navigateToSignInPage();
  }
};
